import {defineStore} from "pinia";
import {OrganizationService} from "@/javascript/services/organization.service";
import {UserService} from "@/javascript/services/user.service";
import {DonorService} from "@/javascript/services/donor.service";
import {HouseholdService} from "../services/household.service";
import {ReportService} from "../services/report.service";

export const useReportStore = defineStore('report', {
  state: () => ({
    // donorService: new DonorService(),
    donorService: null,
    organizationService: new OrganizationService(),
    reportService: null,
    userService: new UserService(),
    organizationId: null,
    donors: [],
    donorsGivingYear: undefined, //tracks the giving_year the cached donors were fetched for
    reports: [],
    currentOrganization: "null",
    loading: true,
    modelName: "Report",
    reportTypes: {
      "DONATION_LIST": "Giving Report",
      "DONOR_LIST": "Donor List",
      "DONOR_YEAR_END_SUMMARY_GROUP": "Year End Summaries",
      "DONOR_CONTRIBUTION_GROUP": "Contribution Statements",
      "GIVING_TREND": "Giving Trends",
      "HOUSEHOLD_MEMBER_LIST": "Member List",
      "HOUSEHOLD_REPORT_TAX_YEAR": "Household Giving Summary"
    },
    meta: {
      page: 1,
      pages: 1,
      limit: 8
    },
    filter: {
      sort: {
        key: "created_at",
        order: "desc"
      },
      search: {
        key: "user_first_name_or_user_last_name_or_user_email_cont", // Ransack search query, add more here to expand the search results
        term: null
      },
      filters: {
        type: ''
      }
    },
  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        if (this.organizationId !== id) {
          this.donors = [];  // Clear cached donors from previous org
        }
        this.organizationId = id;
        this.donorService = new DonorService(id);
        this.reportService = new ReportService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    getReports(skipLoading = false) {
      if(!skipLoading) {
        this.loading = true;
      }
      let params = new URLSearchParams();
      params.set('query[status_in][]', 'COMPLETED');
      params.append('query[status_in][]', 'CREATED');
      params.append('query[status_in][]', 'PROCESSING');
      params.append('query[status_in][]', 'ERROR');

      return this.reportService.paginate(this.meta.page, params, this.filter)
        .then((res) => {
          this.reports = res.data;
          this.meta = res.meta;
        }).catch((e) => {
          console.warn("Error fetching reports", e);
        }).finally(() => {
          this.loading = false;
          return true;
        });
    },

    // Loads the donor list for report exporters. When givingYear is provided
    // (year-scoped reports), the list is limited to donors who gave that year
    // (WI-006). The cache is keyed by year, so switching years re-fetches
    // instead of serving a stale list.
    getUnscopedDonors(givingYear = null) {
      if (this.donors?.length > 0 && this.donorsGivingYear === givingYear) {
        return Promise.resolve(this.donors); // Prevent multiple index calls for the same year
      }
      const params = new URLSearchParams();
      if (givingYear) {
        params.set('giving_year', givingYear);
      }
      return this.donorService.indexAll(params)
        .then((res) => {
          this.donors = res.data;
          this.donorsGivingYear = givingYear;
          return res.data;
        });
    },

    // Loads households for report exporters. When givingYear is provided
    // (year-scoped household reports)) the list is limited to households with at
    // least one member who gave that year (WI-006, DONATE-1596).
    getHouseholds(givingYear = null) {
      const householdService = new HouseholdService(this.organizationId);
      const params = new URLSearchParams();
      if (givingYear) {
        params.set('giving_year', givingYear);
      }
      return householdService.indexAll(params)
        .then((res) => {
          return res.data;
        });
    },

    //** DONOR REPORTS
    createDonorList(donors) {
      const ids = donors.map((d) => d.id);
      return this.reportService.createDonorList(ids)
        .then((res) => {
          if (res.json?.record) {
            return res.json.record;
          } else {
            throw new Error(res.json?.error || "Unable to generate donor list");
          }
        });
    },

    //** DONATION REPORTS
    exportDonationList(params) {
      return this.reportService.createDonationList(params)
        .then((res) => {
          if (res.json?.record) {
            return res.json.record;
          } else {
            throw new Error(res.json?.error || "Unable to generate donation list");
          }
        });
    },


    createDonorDonationReport(donor, year) {
      return this.reportService.donorDonationReportPdf(donor.id, year)
        .then((result) => {
          // WI-007: donor had no giving that year - skipped, not an error. No report generated.
          if (result?.skipped) {
            donor.pendingReportStatus = "SKIPPED";
            return null;
          }
          if (result && result.record && result.record.id) {
            const reportId = result?.record?.id;

            donor.pendingReportId = reportId;
            donor.pendingReportStatus = "CREATED";
            return reportId;
          } else {
            throw new Error(result?.error || "Unable to generate report");
          }
        });
    },

    createDonorYearEndSummaryReport(donor, year) {
      return this.reportService.donorYearEndSummaryReport(donor.id, year)
        .then((result) => {
          // WI-007: donor had no giving that year - skipped, not an error. No report generated.
          if (result?.skipped) {
            donor.pendingReportStatus = "SKIPPED";
            return null;
          }
          const reportId = result?.record?.id;
          if (reportId) {
            // async generation successful, wait for download
            donor.pendingReportId = reportId;
            donor.pendingReportStatus = "CREATED";
            return reportId;
          } else {
            throw new Error(result?.error || "Unable to generate report");
          }
        });
    },

    // HOUSEHOLD REPORTS
    createHouseholdMemberList(household) {
      return this.reportService.householdMemberList(household.id)
        .then((res) => {
          if (res.json?.record?.id) {
            household.pendingReportId = res.json.record.id;
            household.pendingReportStatus = "CREATED";
            return res.json.record.id;
          } else {
            throw new Error(res.json?.error || "Unable to generate member list");
          }
        });
    },

    createHouseholdDonationSummary(household, year) {
      return this.reportService.householdDonationSummary(household.id, year)
        .then((res) => {
          if (res.json?.record?.id) {
            household.pendingReportId = res.json.record.id;
            household.pendingReportStatus = "CREATED";
            return res.json.record.id;
          } else {
            throw new Error(res.json?.error || "Unable to generate household summary");
          }
        });
    },



    // Async zip: sends report IDs to backend, gets a parent report, polls until zip is ready, then downloads.
    downloadMultipleReportsZipped(reportIds, donors = []) {
      // Scale polling timeout based on number of reports (10s per poll, ~2 min per 100 donors)
      const maxAttempts = Math.max(30, Math.ceil(reportIds.length / 10));

      return this.reportService.downloadReportsZip(reportIds)
        .then((result) => {
          if (!result || !result.record) {
            throw new Error("Failed to start report download. Please try again.");
          }
          return this.pollReport(result.record.id, 1, maxAttempts);
        })
        .then((record) => {
          if (record) {
            const url = `${this.reportService.endpoint}/${record.id}/download_report`;
            const filename = `reports_${new Date().toISOString().split('T')[0]}.zip`;
            this.reportService.downloadFile(url, filename);
          } else {
            throw new Error("Report generation failed or timed out.");
          }
          return true;
        });
    },

    downloadDonorReport(reportId, orgName, year) {
      return this.reportService.downloadReportPdf(reportId, orgName, year)
        .then((result) => {
          return result;
        });
    },

    createGivingTrendReport(donorIds, period1Start, period1End, period2Start, period2End) {
      return this.reportService.createGivingTrendReport(donorIds, period1Start, period1End, period2Start, period2End)
        .then((res) => {
          if (res.json?.record) {
            return res.json.record;
          } else {
            throw new Error(res.json?.error || "Unable to generate giving trend report");
          }
        });
    },

    // Poll a report until it's ready or failed
    // Increased length between attempts until max attempts is reached
    pollReport(reportId, attempt = 1, maxAttempts = 12) {
      // Get the report and check its status
      return this.reportService.getReport(reportId).then((result) => {
        const record = result?.record;
        if (record) {
          if (record.status?.toLowerCase() === 'failed') {
            this.generatingDonorReport = false;
            return false;
          } else if (record.status?.toLowerCase() === 'completed') {
            // The report is complete, download the PDF file
            return record;
          } else {
            // Report is still processing
            if (attempt >= maxAttempts) {
              throw new Error('Report generation timeout - maximum polling attempts reached');
            }

            // Simple exponential backoff: 2s, 4s, 8s, 16s, 32s, 64s...
            const delay = Math.min(Math.pow(2, attempt) * 1000, 10000);

            // Wait before next attempt, then recursive call
            return new Promise(resolve => setTimeout(resolve, delay))
              .then(() => this.pollReport(reportId, attempt + 1, maxAttempts));
          }
        } else {
          // The record wasn't found
          return false;
        }
      }).catch((error) => {
        console.error('Error polling report:', error);
        throw error;
      });
    },
    
    downloadCSVReport(report) {
      return this.reportService.downloadReportFile(report).catch((error) => {
        console.error('Report download failed:', error.message);
        throw error;
      });
    },

    filterRecords(key, value) {
      this.filter.filters[key] = value;
      if(key === 'report_type') {
        this.filter.filters[key] = Object.entries(this.reportTypes).find(([k, v]) => v === value)?.[0];
      }
      this.getReports();
    },

    //** Common Index Methods
    nextPage() {
      this.meta.page++;
      this.getReports();
    },
    prevPage() {
      this.meta.page--;
      this.getReports();
    },
    moveToPage(page) {
      this.meta.page = page;
      this.getReports();
    },
  },

  getters: {
    getIndexTableData(state) {

      let labels = [
        {
          label: 'Type',
          key: 'report_type',
          sort: false,
          select: true,
          options: Object.values(state.reportTypes)},
        {label: "Status", key: 'status', sort: false},
        {label: "Date", key: 'created_at', sort: false, format: "date"},
        {label: "Created by", key: 'created_by', sort: false},
        {
          label: "Link",
          key: 'document_link',
          sort: false,
          format: "download-link",
          error: (report) => {
            return report.status === 'ERROR';
          },
          loading: (report) => {
            return report.status !== 'COMPLETED' && report.status !== 'ERROR';
          }
        },
      ];
      if (state.reports.length > 0) {
        let reports = state.reports.map((report) => {
          return {
            ...report,
            report_type: state.reportTypes[report.report_type],
          };
        });
        return {
          labels: labels,
          rows: reports
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
  }
});
